import React, { useState, useEffect } from "react";
import "./styles.css";
import { Icons } from "./components/Icons";
import {
  Logo,
  FeatureCard,
  MiniFeature,
  FlipCard,
} from "./components/UIComponents";
import { useCasesList, testimonials, rotatingWords } from "./constants/data";

const HelplyLanding = () => {
  const [scrolled, setScrolled] = useState(false);
  const [text, setText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const currentWord = rotatingWords[wordIndex];
    const typingSpeed = isDeleting ? 50 : 120;
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        const nextText = currentWord.slice(0, text.length + 1);
        setText(nextText);
        if (nextText === currentWord)
          setTimeout(() => setIsDeleting(true), 1200);
      } else {
        const nextText = currentWord.slice(0, text.length - 1);
        setText(nextText);
        if (nextText === "") {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % rotatingWords.length);
        }
      }
    }, typingSpeed);
    return () => clearTimeout(timeout);
  }, [text, isDeleting, wordIndex]);

  return (
    <div className="min-h-screen font-sans text-slate-800 bg-white overflow-x-hidden">
      {/* --- Header --- */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-md shadow-sm py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 max-w-7xl flex justify-between items-center">
          {/* --- LOGO IMPLEMENTATION --- */}
          <Logo size={scrolled ? "md" : "xl"} />

          <div className="hidden md:flex gap-8 text-lg font-semibold text-slate-700">
            <a href="#features" className="hover:text-blue-600 transition">
              Features
            </a>
            <a href="#testimonials" className="hover:text-blue-600 transition">
              Testimonials
            </a>
            <a href="#about" className="hover:text-blue-600 transition">
              About Us
            </a>
          </div>

          <button className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full text-lg font-bold transition shadow-md shadow-green-100 transform hover:scale-105 active:scale-95">
            Launch WhatsApp
          </button>
        </div>
      </nav>

      {/* --- Hero Section --- */}
      <section className="relative min-h-[95vh] pt-28 flex items-center bg-[#F8FAFC] overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-64 bg-gradient-to-b from-blue-50 to-transparent"></div>
        <div className="absolute top-1/3 right-[-10%] w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-3xl -z-10 animate-pulse"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-green-100/40 rounded-full blur-3xl -z-10"></div>

        <div className="container mx-auto px-6 max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pb-20">
          {/* Hero Content */}
          <div className="space-y-8 max-w-xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white border border-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              AI-Powered Memory
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 leading-[1.1] tracking-tight">
              Never forget <br />
              <span className="text-blue-600 underline-offset-8">
                {text}
                <span className="animate-pulse">|</span>
              </span>{" "}
              <br />
              again.
            </h1>

            <h2 className="text-2xl md:text-3xl text-slate-600 font-medium">
              Your personal assistant, living inside{" "}
              <span className="text-green-500 font-bold">WhatsApp</span>.
            </h2>

            <p className="text-lg text-slate-500 leading-relaxed max-w-lg">
              Just chat like you do with a friend. We handle the schedules,
              reminders, and mental load so you don't have to.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <button className="bg-slate-900 text-white px-8 py-4 rounded-xl text-lg font-bold hover:bg-slate-800 transition shadow-xl shadow-slate-200/50 transform hover:-translate-y-1">
                Start Chatting Now
              </button>
              <button className="px-8 py-4 rounded-xl text-lg font-bold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition flex items-center gap-2 font-semibold">
                <Icons.Brain className="w-5 h-5 text-blue-500" /> See examples
              </button>
            </div>
          </div>

          {/* Hero Action Card */}
          <div className="relative perspective-1000 hidden lg:block">
            <div className="absolute top-[10%] -right-[5%] w-20 h-20 bg-yellow-200 rounded-2xl rotate-12 -z-10 animate-bounce-slow opacity-70 font-bold flex items-center justify-center text-2xl">
              📅
            </div>
            <div className="absolute bottom-[15%] -left-[5%] w-24 h-24 bg-green-200 rounded-full -z-10 animate-bounce-slow delay-700 opacity-70 font-bold flex items-center justify-center text-3xl">
              💊
            </div>

            <div className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] shadow-[0_20px_50px_rgba(8,_112,_184,_0.1)] border border-white/50 relative z-10 max-w-md mx-auto lg:ml-auto transform rotate-y-6 hover:rotate-y-0 transition-all duration-500 ease-out">
              <div className="absolute -top-5 -left-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg transform -rotate-6 z-20">
                ⚡ Setup in 10 seconds
              </div>

              <div className="flex flex-col items-center mb-6">
                <div className="bg-gradient-to-tr from-slate-50 to-white p-4 rounded-3xl border-2 border-dashed border-blue-200 w-48 h-48 flex justify-center items-center relative shadow-sm group">
                  <div className="w-36 h-36 group-hover:scale-105 transition-transform duration-300">
                    <Icons.QrCode />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-green-500 p-3 rounded-full border-[5px] border-white shadow-xl animate-pulse-slow">
                      <Icons.MessageCircle className="text-white w-6 h-6" />
                    </div>
                  </div>
                </div>
                <p className="text-xs font-bold text-slate-400 mt-4 uppercase tracking-widest">
                  Scan to launch WhatsApp
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Features Section --- */}
      <section id="features" className="py-24 bg-white relative">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-20 max-w-3xl mx-auto leading-relaxed">
            <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4">
              Features
            </h2>
            <h3 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Your brain, outsourced.
            </h3>
            <p className="text-slate-500 text-xl">
              We stripped away the complex apps and interfaces. Just natural
              conversation handled by powerful AI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
            <FeatureCard
              icon={<Icons.CheckSquare className="w-7 h-7 text-white" />}
              title="Natural Language Tasks"
              desc="Just say 'Remind me to call Mom tomorrow at 5pm'. We parse the intent, time, and date instantly."
              color="bg-blue-500"
            />
            <FeatureCard
              icon={<Icons.Brain className="w-7 h-7 text-white" />}
              title="Contextual Memory"
              desc="The assistant remembers past conversations. Asking 'When is that doctor appointment again?' actually works."
              color="bg-purple-500"
            />
            <FeatureCard
              icon={<Icons.Mic className="w-7 h-7 text-white" />}
              title="Voice Note Whisperer"
              desc="Driving or cooking? Send a quick voice note. We transcribe it and set the reminder automatically."
              color="bg-green-500"
            />
          </div>
        </div>

        {/* --- Infinite Marquee Section --- */}
        <div className="bg-slate-50 py-16 overflow-hidden relative">
          <div className="absolute inset-y-0 left-0 w-20 md:w-40 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute inset-y-0 right-0 w-20 md:w-40 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none"></div>

          <div className="container mx-auto px-6 max-w-7xl mb-10 text-center relative z-20">
            <h3 className="text-2xl font-bold text-slate-800">
              Everything else you need to organize
            </h3>
            <p className="text-slate-500 mt-2">
              If you can text it, we can remind you.
            </p>
          </div>

          <div className="flex flex-col gap-6 relative z-0">
            <div className="flex overflow-hidden">
              <div className="flex animate-scroll-left gap-4 min-w-full flex-shrink-0 items-center">
                {[...useCasesList, ...useCasesList].map((item, index) => (
                  <MiniFeature
                    key={`r1-${index}`}
                    icon={item.icon}
                    label={item.label}
                  />
                ))}
              </div>
            </div>
            <div className="flex overflow-hidden">
              <div className="flex animate-scroll-right gap-4 min-w-full flex-shrink-0 items-center ml-[-50px]">
                {[...useCasesList.reverse(), ...useCasesList.reverse()].map(
                  (item, index) => (
                    <MiniFeature
                      key={`r2-${index}`}
                      icon={item.icon}
                      label={item.label}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Testimonials Section with 3D Flip --- */}
      <section
        id="testimonials"
        className="py-24 bg-[#0F172A] text-white relative overflow-hidden"
      >
        <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-green-600/20 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <div className="max-w-2xl">
              <h2 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-4">
                Testimonials
              </h2>
              <h3 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight">
                Loved by the <br />
                organized (and formerly chaotic).
              </h3>
              <p className="text-slate-400">
                Hover over the cards to see the before & after transformation.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <FlipCard key={idx} data={t} />
            ))}
          </div>
        </div>
      </section>

      {/* --- About Us Section --- */}
      <section id="about" className="py-20 bg-white border-t border-slate-100">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="inline-block p-3 rounded-2xl bg-slate-50 mb-6">
            <span className="text-4xl">🤝</span>
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-6">About Us</h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            We are a small team of engineers and designers who were tired of
            downloading a new app for every single habit we wanted to track. We
            believe technology should be invisible.
            <br />
            <br />
            This product was born from a simple idea:{" "}
            <strong>
              What if your to-do list lived where you already spend your time?
            </strong>{" "}
            No logins, no downloads, just a helpful friend in your contact list.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t border-slate-100">
            <div className="p-4">
              <div className="text-3xl font-bold text-blue-600 mb-1">10k+</div>
              <div className="text-sm text-slate-500 font-medium">
                Active Users
              </div>
            </div>
            <div className="p-4">
              <div className="text-3xl font-bold text-blue-600 mb-1">1.2M</div>
              <div className="text-sm text-slate-500 font-medium">
                Reminders Sent
              </div>
            </div>
            <div className="p-4">
              <div className="text-3xl font-bold text-blue-600 mb-1">99.9%</div>
              <div className="text-sm text-slate-500 font-medium">Uptime</div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Footer --- */}
      <footer className="bg-slate-50 pt-16 pb-12 border-t border-slate-200">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <Logo size="lg" />
              </div>
              <p className="text-slate-500 leading-relaxed max-w-sm mb-6 font-medium">
                Making humanity more organized, one WhatsApp message at a time.
              </p>
              <div className="flex gap-4">
                <a
                  href="#"
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 hover:border-blue-200 transition"
                >
                  <Icons.Twitter />
                </a>
                <a
                  href="https://www.instagram.com/shrey_2802/"
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 hover:border-pink-200 transition"
                >
                  <Icons.Instagram />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-6">Product</h4>
              <ul className="space-y-4 text-sm font-medium text-slate-500">
                <li>
                  <a href="#" className="hover:text-blue-600 transition">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-600 transition">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-600 transition">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-6">Legal</h4>
              <ul className="space-y-4 text-sm font-medium text-slate-500">
                <li>
                  <a
                    href="#!/privacy"
                    className="hover:text-blue-600 transition"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#!/terms" className="hover:text-blue-600 transition">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="#!/data-deletion"
                    className="hover:text-blue-600 transition"
                  >
                    Data Deletion Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8 flex flex-col md:flex-row justify-between items-center text-sm font-medium text-slate-400">
            <p>© 2026. All rights reserved.</p>
            <p>Cleverly engineered for busy brains.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelplyLanding;
